import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Upload, X } from 'lucide-react';

interface ComplaintsFormProps {
  onBack?: () => void;
  onSubmit?: (data: ComplaintFormData) => void;
}

interface ComplaintFormData {
  title: string;
  description: string;
  machineId: string;
  currentCounter: number;
  attachments: File[];
}

export const ComplaintsForm = ({ onBack, onSubmit }: ComplaintsFormProps) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<ComplaintFormData>({
    title: '',
    description: '',
    machineId: '',
    currentCounter: 0,
    attachments: []
  });

  // Mock machines data - in real app this would come from API
  const machines = [
    { id: 'hp001', name: 'HP LaserJet Pro MFP M428fdw', serialNumber: 'HP001234' },
    { id: 'canon001', name: 'Canon imageRUNNER ADVANCE C3330i', serialNumber: 'CAN005678' },
    { id: 'xerox001', name: 'Xerox WorkCentre 6515', serialNumber: 'XER009876' },
    { id: 'brother001', name: 'Brother MFC-L8900CDW', serialNumber: 'BRO005432' },
    { id: 'hp002', name: 'HP Color LaserJet Pro MFP M479fdw', serialNumber: 'HP007890' }
  ];

  const handleInputChange = (field: keyof ComplaintFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf', 'text/plain'];

    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        toast({
          title: "Fichier trop volumineux",
          description: `${file.name} dépasse la taille limite de 10MB`,
          variant: "destructive"
        });
        return false;
      }

      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Type de fichier non supporté",
          description: `${file.name} n'est pas un type de fichier supporté`,
          variant: "destructive"
        });
        return false;
      }

      return true;
    });

    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast({
        title: "Titre requis",
        description: "Veuillez saisir un titre pour votre réclamation",
        variant: "destructive"
      });
      return;
    }

    if (!formData.description.trim()) {
      toast({
        title: "Description requise",
        description: "Veuillez saisir une description pour votre réclamation",
        variant: "destructive"
      });
      return;
    }

    if (!formData.machineId) {
      toast({
        title: "Machine requise",
        description: "Veuillez sélectionner une machine",
        variant: "destructive"
      });
      return;
    }

    if (formData.currentCounter <= 0) {
      toast({
        title: "Compteur invalide",
        description: "Veuillez saisir un compteur valide",
        variant: "destructive"
      });
      return;
    }

    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Réclamation créée",
        description: "Votre réclamation a été créée avec succès. Un agent vous contactera bientôt."
      });
      
      onSubmit?.(formData);
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        machineId: '',
        currentCounter: 0,
        attachments: []
      });
    }, 1000);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <CardTitle>Nouvelle réclamation</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Titre de la réclamation *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Ex: Problème de qualité d'impression"
              required
            />
          </div>

          {/* Machine Selection */}
          <div className="space-y-2">
            <Label htmlFor="machine">Machine concernée *</Label>
            <Select value={formData.machineId} onValueChange={(value) => handleInputChange('machineId', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une machine" />
              </SelectTrigger>
              <SelectContent>
                {machines.map((machine) => (
                  <SelectItem key={machine.id} value={machine.id}>
                    {machine.name} - {machine.serialNumber}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Current Counter */}
          <div className="space-y-2">
            <Label htmlFor="counter">Compteur actuel *</Label>
            <Input
              id="counter"
              type="number"
              min="0"
              value={formData.currentCounter || ''}
              onChange={(e) => handleInputChange('currentCounter', parseInt(e.target.value) || 0)}
              placeholder="Ex: 15420"
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description détaillée *</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Décrivez en détail le problème rencontré..."
              rows={6}
              required
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <Label htmlFor="attachments">Pièces jointes</Label>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  id="attachments"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('attachments')?.click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Ajouter des fichiers
                </Button>
                <span className="text-sm text-muted-foreground">
                  JPG, PNG, GIF, PDF, TXT (max 10MB par fichier)
                </span>
              </div>

              {/* File List */}
              {formData.attachments.length > 0 && (
                <div className="space-y-2">
                  <Label>Fichiers sélectionnés:</Label>
                  <div className="space-y-2">
                    {formData.attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-muted-foreground">({formatFileSize(file.size)})</span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAttachment(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-2 pt-4">
            <Button type="submit" className="flex-1">
              Créer la réclamation
            </Button>
            {onBack && (
              <Button type="button" variant="outline" onClick={onBack}>
                Annuler
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ComplaintsForm;